"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TriangleAlert as AlertTriangle, Droplets, Wind, Trees, CircleAlert, PhoneCall, ArrowRight, ShieldAlert } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
interface SewerSignItem {
  id: string;
  iconName: string;
  title: string;
  subtitle: string;
  description: string;
  urgency: "High" | "Critical";
  indicator: string;
}
const SEWER_SIGNS: SewerSignItem[] = [{
  id: "slow-drains",
  iconName: "droplets",
  title: "Multiple Slow Drains",
  subtitle: "Simultaneous drainage slowdowns",
  description: "When sinks, tubs, and showers across your home drain slowly at the same time, the blockage is typically deep inside the main underground sewer pipe rather than a single fixture.",
  urgency: "High",
  indicator: "Gurgling sounds in secondary sinks when running the washing machine"
}, {
  id: "foul-odors",
  iconName: "wind",
  title: "Persistent Sewer Odors",
  subtitle: "Sulfur or rotten egg smell indoors",
  description: "A cracked or cracked main line lets hazardous sewer gas escape through vent stacks, floor drains, and crawlspaces, creating strong sulfur or decomposing odors throughout the home.",
  urgency: "High",
  indicator: "Smells that intensify near bathroom drains and basement corners"
}, {
  id: "soggy-lawn",
  iconName: "trees",
  title: "Soggy or Unusually Green Lawn",
  subtitle: "Underground soil saturation",
  description: "Underground sewer leaks release wastewater beneath your yard. This creates standing damp pools, spongy mud patches, and unusually vibrant green grass directly above the buried pipe.",
  urgency: "High",
  indicator: "Indented sinkholes or damp yard patches during dry weather"
}, {
  id: "sewage-backup",
  iconName: "circle-alert",
  title: "Direct Sewage Backup",
  subtitle: "Wastewater surfacing in lowest fixtures",
  description: "Raw wastewater coming up through basement floor drains, shower bases, or first-floor toilets is a true plumbing emergency that requires immediate line clearing to stop property contamination.",
  urgency: "Critical",
  indicator: "Dark water or toilet waste surfacing when running household taps"
}];
export default function SewerSigns() {
  const shouldReduceMotion = useReducedMotion();
  const getIcon = (name: string) => {
    switch (name) {
      case "droplets":
        return <Droplets className="size-6 text-primary" />;
      case "wind":
        return <Wind className="size-6 text-primary" />;
      case "trees":
        return <Trees className="size-6 text-primary" />;
      case "circle-alert":
        return <CircleAlert className="size-6 text-destructive" />;
      default:
        return <AlertTriangle className="size-6 text-primary" />;
    }
  };
  return <section id="sewerproblemsigns" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="outline" className="mb-4 px-3 py-1 font-medium text-xs uppercase tracking-wide">
            Early Warning Indicators
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground mb-4">
            Signs of a Broken or Clogged Sewer Line
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Main sewer pipe failures get worse quickly. Identifying these common symptoms early helps prevent catastrophic wastewater flooding and expensive foundation damage.
          </p>
        </div>

        {/* 4 Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-12">
          {SEWER_SIGNS.map((sign, index) => <motion.div key={sign.id} data-index={index} initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-50px"
        }} transition={{
          duration: 0.4,
          delay: index * 0.1
        }} className="h-full">
              <Card className="h-full border border-border shadow-sm hover:shadow-md transition-shadow duration-200 bg-card text-card-foreground flex flex-col justify-between">
                <CardHeader className="space-y-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="p-3 rounded-lg bg-secondary text-secondary-foreground inline-flex items-center justify-center">
                      {getIcon(sign.iconName)}
                    </div>
                    <Badge variant={sign.urgency === "Critical" ? "destructive" : "secondary"} className="font-medium text-xs uppercase">
                      {sign.urgency} Urgency
                    </Badge>
                  </div>
                  <div>
                    <CardTitle className="text-xl sm:text-2xl font-default font-semibold text-card-foreground">
                      {sign.title}
                    </CardTitle>
                    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mt-1">
                      {sign.subtitle}
                    </p>
                  </div>
                  <CardDescription className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                    {sign.description}
                  </CardDescription>
                </CardHeader>

                <CardContent className="pt-2">
                  <div className="p-3.5 rounded-lg bg-background border border-border/80 text-xs sm:text-sm text-foreground/90 flex items-start gap-2.5">
                    <AlertTriangle className="size-4 shrink-0 text-accent mt-0.5" />
                    <div>
                      <span className="font-semibold text-foreground">Common indicator: </span>
                      <span className="text-muted-foreground">{sign.indicator}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </div>

        {/* Emergency Alert + Call to Action Banner */}
        <div className="max-w-4xl mx-auto">
          <Alert className="border-border bg-card p-6 sm:p-8 rounded-xl shadow-sm">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 w-full">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-lg bg-destructive/10 text-destructive shrink-0 mt-1 sm:mt-0">
                  <ShieldAlert className="size-6 text-destructive" />
                </div>
                <div>
                  <AlertTitle className="text-lg sm:text-xl font-default font-bold text-card-foreground">
                    Experiencing Active Wastewater Backups?
                  </AlertTitle>
                  <AlertDescription className="text-sm text-muted-foreground mt-1 leading-relaxed">
                    Turn off all water fixtures immediately to avoid overflow. Our Jonesboro emergency plumbing team is available around the clock with sewer cameras and hydro-jetting equipment.
                  </AlertDescription>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row lg:flex-col gap-3 w-full lg:w-auto shrink-0">
                <Button size="lg" className="w-full sm:w-auto" asChild>
                  <a href="tel:4708479076">
                    <PhoneCall className="size-4 mr-2" />
                    Call 24/7 Dispatch
                  </a>
                </Button>
                <Button variant="outline" size="lg" className="w-full sm:w-auto" asChild>
                  <Link to="/contact/">
                    Schedule Camera Inspection
                    <ArrowRight className="size-4 ml-2" />
                  </Link>
                </Button>
              </div>
            </div>
          </Alert>
        </div>
      </div>
    </section>;
}